import { useCallback, useState } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import messages from './messages';
import style from './style.module.less';
import UnexpectedValueComponent from './unexpected-value';
import { CountrySearchableDropdown } from '../../../../../../../../components/country-dropdown/country-searchable-dropdown';
import { PARAMETER } from '../../../../../constants/parameter-name-for-update';
import InputValueProps from '../../../../../prop-types/input-value-props';

const ACCOUNT_NUMBER_FIELD_ID = 'third-party-shipping-account-number';
const BILLING_COUNTRY_FIELD_ID = 'third-party-shipping-billing-country';
const BILLING_POSTAL_CODE_FIELD_ID = 'third-party-shipping-billing-postal-code';

const ChargeShippingToThirdParty = ({ inputValueProps, onChange }) => {
  const { parameter } = inputValueProps;
  const isNewAction = parameter === undefined;
  const valueParts = parameter?.toString().split('|') || [];
  const [accountNumber, setAccountNumber] = useState(valueParts[0] || '');
  const [countryCode, setCountryCode] = useState(valueParts[1] || '');
  const [billingPostalCode, setBillingPostalCode] = useState(valueParts[2] || '');
  const valueDoesNotConformToStandard = !isNewAction && valueParts.length !== 3;

  const updateValue = useCallback(
    ({ newAccountNumber, newCountryCode, newBillingPostalCode }) => {
      const accountNumberPart = newAccountNumber || accountNumber;
      const countryCodePart = newCountryCode || countryCode;
      const billingPostalCodePart = newBillingPostalCode || billingPostalCode;

      onChange({
        [PARAMETER]: `${accountNumberPart}|${countryCodePart}|${billingPostalCodePart}`,
      });
    },
    [accountNumber, billingPostalCode, countryCode, onChange],
  );

  const onSetAccountNumber = useCallback(
    (e) => {
      setAccountNumber(e?.target?.value);
    },
    [setAccountNumber],
  );

  const onBlurAccountNumber = useCallback(() => {
    updateValue({ newAccountNumber: accountNumber });
  }, [accountNumber, updateValue]);

  const onSetBillingPostalCode = useCallback(
    (e) => {
      setBillingPostalCode(e?.target?.value);
    },
    [setBillingPostalCode],
  );

  const onBlurBillingPostalCode = useCallback(() => {
    updateValue({ newBillingPostalCode: billingPostalCode });
  }, [billingPostalCode, updateValue]);

  const updateBillingCountry = useCallback(
    (code) => {
      setCountryCode(code);
      updateValue({ newCountryCode: code });
    },
    [updateValue],
  );

  if (valueDoesNotConformToStandard) {
    return <UnexpectedValueComponent />;
  }
  return (
    <div className={style['multi-control']}>
      <div className={style['multi-control-row']}>
        <div>
          <label htmlFor={ACCOUNT_NUMBER_FIELD_ID}>
            <FormattedMessage {...messages.chargeThirdPartyAccountLabel} />
          </label>
        </div>
        <div>
          <input
            className={style['text-input']}
            id={ACCOUNT_NUMBER_FIELD_ID}
            onBlur={onBlurAccountNumber}
            onChange={onSetAccountNumber}
            value={accountNumber}
          />
        </div>
      </div>
      <div className={classnames(style.stacked, style['multi-control-row'])}>
        <div>
          <label htmlFor={BILLING_COUNTRY_FIELD_ID}>
            <FormattedMessage {...messages.chargeThirdPartyCountryLabel} />
          </label>
        </div>
        <div>
          <CountrySearchableDropdown
            id={BILLING_COUNTRY_FIELD_ID}
            onCountrySelected={updateBillingCountry}
            placeholderMessageDescriptor={messages.noOptionSelectedMessage}
            selectedCountryCode={countryCode}
          />
        </div>
      </div>
      <div className={classnames(style.stacked, style['multi-control-row'])}>
        <div>
          <label htmlFor={BILLING_POSTAL_CODE_FIELD_ID}>
            <FormattedMessage {...messages.chargeThirdPartyPostalCodeLabel} />
          </label>
        </div>
        <div>
          <input
            className={style['text-input']}
            id={BILLING_POSTAL_CODE_FIELD_ID}
            onBlur={onBlurBillingPostalCode}
            onChange={onSetBillingPostalCode}
            value={billingPostalCode}
          />
        </div>
      </div>
    </div>
  );
};

ChargeShippingToThirdParty.propTypes = {
  inputValueProps: InputValueProps.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default ChargeShippingToThirdParty;
